/* lib/academy/pricing.js — цена и условия тарифа словами покупателя (AD-9).
 *
 * Один источник правды для трёх поверхностей продажи: блок «Тарифы» лендинга,
 * главная CTA лендинга и форма покупки (BuyDialog). Они расходились: карточка
 * тарифа подписывала режим ДЗ по derived-флагу `homework_auto_accept` (третий
 * режим `auto_after_feedback` читался как «проверка куратором» и обещал разбор
 * работ, которого после окна ОС не будет), а кнопка покупки при нескольких
 * тарифах печатала цену КУРСА — заведомо неверную цифру. */

/** Деньги в рублях без копеек: «5 900 ₽». */
export function money(v) {
  return new Intl.NumberFormat('ru-RU').format(Math.round(Number(v || 0))) + ' ₽';
}

/** Сумма фактического платежа: копейки показываем, если они есть. Это история
 *  оплат, а не витрина, — округлять чужой чек нельзя. */
export function moneyExact(v) {
  // Пустое значение — это «суммы нет», а Number('')/Number(null) дают 0: без
  // отдельной проверки история оплат показывала бы честный ноль вместо прочерка.
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  // Копейки либо обе, либо ни одной: «5 900,5 ₽» читается как обрезанная сумма.
  const kopecks = Math.abs(n % 1) > 0.0001;
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: kopecks ? 2 : 0, maximumFractionDigits: 2,
  }).format(n) + ' ₽';
}

/** Режим приёма ДЗ по тарифу. Три значения, не два: `auto_after_feedback` —
 *  «сначала куратор, потом автоматически». `homework_auto_accept` остаётся
 *  фолбэком для старых payload'ов, где режима ещё нет. */
export function homeworkModeLabel(tariff) {
  const t = tariff || {};
  // Тариф «без ДЗ» — проверять нечего, и любая подпись про режим приёма здесь
  // соврала бы покупателю («Проверка ДЗ куратором» у тарифа без заданий).
  // Ветка идёт ДО деривации режима сознательно.
  if (t.homework_enabled === false) return 'Без домашних заданий';
  const mode = t.homework_review_mode || (t.homework_auto_accept ? 'auto' : 'curator');
  if (mode === 'auto') return 'ДЗ засчитывается автоматически';
  if (mode === 'auto_after_feedback') {
    return t.feedback_days
      ? `Проверка куратором ${t.feedback_days} дн., дальше — автоматически`
      : 'Проверка куратором, после окончания ОС — автоматически';
  }
  // Окно ОС на тарифе `curator` — это ещё и срок ПРИЁМА работ: после него сдать
  // ДЗ нельзя вовсе (services.feedback_closed). Покупатель должен узнать про
  // этот срок до оплаты, а не упершись в него на середине курса.
  return tariff && tariff.feedback_days
    ? `Проверка куратором, приём работ ${tariff.feedback_days} дн.`
    : 'Проверка ДЗ куратором';
}

/** Срок доступа по тарифу словами. */
export function accessDaysLabel(tariff) {
  const days = tariff && tariff.access_days;
  return days ? `Доступ ${days} дн.` : 'Доступ навсегда';
}

/** Минимальная цена среди тарифов (null — тарифов нет). */
export function minTariffPrice(tariffs) {
  const list = (Array.isArray(tariffs) ? tariffs : [])
    .map((t) => Number(t && t.price_rub))
    .filter((n) => Number.isFinite(n));
  return list.length ? Math.min(...list) : null;
}

/** Подпись кнопки покупки на лендинге.
 *  Курс продаётся ПО ТАРИФАМ, если они есть, — цена курса в этом случае не
 *  применяется вовсе (`purchasing._resolve_tariff`), и печатать её на кнопке
 *  значит называть покупателю сумму, которую с него не спишут. Тариф один —
 *  показываем его цену точно, несколько — «от», точную сумму покупатель видит
 *  на карточке выбранного тарифа и в форме оплаты. */
/** Предвыбор тарифа в форме покупки → { tariffId, lost }.
 *
 *  `lost: true` — покупатель пришёл повторно оплачивать брошенный заказ, а его
 *  тариф методист снял с продажи. Раньше предвыбор в этом случае молча падал на
 *  первый тариф в списке: человек жал «Оплатить» и покупал НЕ ТО, что бросил,
 *  без единого слова. Теперь при нескольких действующих тарифах предвыбор не
 *  делается вовсе (выбор осознанный), при единственном — он и подставляется, но
 *  форма обязана сказать о подмене.
 *
 *  Тарифов нет вовсе (курс продаётся по цене курса) — это не потеря тарифа:
 *  сумму покупатель видит в форме. */
export function resolvePreselectedTariff(tariffs, initialTariffId) {
  const list = Array.isArray(tariffs) ? tariffs : [];
  const first = list.length ? list[0].id : null;
  if (initialTariffId == null || !list.length) return { tariffId: first, lost: false };
  if (list.some((t) => t && t.id === initialTariffId)) return { tariffId: initialTariffId, lost: false };
  return { tariffId: list.length > 1 ? null : first, lost: true };
}

export function buyCtaLabel(course, tariffs) {
  const min = minTariffPrice(tariffs);
  if (min != null) {
    const list = Array.isArray(tariffs) ? tariffs : [];
    return list.length > 1 ? `Купить · от ${money(min)}` : `Купить · ${money(min)}`;
  }
  return `Купить · ${money(course && course.price_rub)}`;
}
